import React, { useState, useEffect } from 'react';
import { CloseOutlined } from '@mui/icons-material';
import { showToast } from '../../atoms/Toast/Toast';
import authService from '../../../services/authService';
import './SupervisorEditModal.css';

const SupervisorEditModal = ({ user, onAdd, onRemove, onClose }) => {
  const initialIds = (
    user.supervisor_ids || (user.supervisors || []).map((s) => s.id)
  ).map(String);

  const [selectedIds, setSelectedIds] = useState(initialIds);
  const [saving, setSaving] = useState(false);

  // ── NOUVEAU : liste chargée via API au lieu de filtrer adminsList ──
  const [filtered, setFiltered] = useState([]);
  const [loadingSupervisors, setLoadingSupervisors] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingSupervisors(true);
      try {
        const data = await authService.getAvailableSupervisors(user.id);
        if (!cancelled) setFiltered(data);
      } catch {
        if (!cancelled) setFiltered([]);
      } finally {
        if (!cancelled) setLoadingSupervisors(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user.id]);

  const required = user.role === 'user';

  const toggle = (id) => {
    const strId = String(id);
    const isSelected = selectedIds.includes(strId);

    if (isSelected) {
      if (required && selectedIds.length === 1) return;
      setSelectedIds((prev) => prev.filter((v) => v !== strId));
    } else {
      setSelectedIds((prev) => [...prev, strId]);
    }
  };

  const hasChanges =
    selectedIds.length !== initialIds.length ||
    !selectedIds.every((id) => initialIds.includes(id));

  const handleSave = async () => {
    if (required && selectedIds.length === 0) {
      showToast('Veuillez sélectionner au moins un superviseur.', 'error');
      return;
    }
    setSaving(true);
    try {
      const oldSet = new Set(initialIds);
      const newSet = new Set(selectedIds);
      const toAdd    = [...newSet].filter((id) => !oldSet.has(id));
      const toRemove = [...oldSet].filter((id) => !newSet.has(id));

      await Promise.all([
        ...toAdd.map((id)    => onAdd(id, user.id)),
        ...toRemove.map((id) => onRemove(id, user.id)),
      ]);
      onClose();
    } catch (e) {
      showToast(e.message || 'Erreur mise à jour des superviseurs', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="supervisor-edit-modal" onClick={(e) => e.stopPropagation()}>

        <div className="supervisor-edit-modal__header">
          <div>
            <h3>Superviseur(s) de {user.name} {user.lastname}</h3>
            <p className="supervisor-edit-modal__desc">
              Qui supervise {user.name} {user.lastname} ?
            </p>
          </div>
          <button className="supervisor-edit-modal__close" onClick={onClose} disabled={saving}>
            <CloseOutlined />
          </button>
        </div>

        <div className="supervisor-edit-modal__body">
          {loadingSupervisors ? (
            <div className="supervisor-edit-modal__loading">
              <span className="supervisor-edit-modal__spinner" />
              Chargement…
            </div>
          ) : filtered.length === 0 ? (
            <div className="supervisor-edit-modal__empty">Aucun admin disponible</div>
          ) : (
            filtered.map((admin) => {
              const strId = String(admin.id);
              const checked = selectedIds.includes(strId);
              const isLastRequired = required && checked && selectedIds.length === 1;

              return (
                <label
                  key={admin.id}
                  className={`supervisor-edit-option${checked ? ' supervisor-edit-option--checked' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(admin.id)}
                    disabled={isLastRequired || saving}
                  />
                  <span className="supervisor-edit-option__name">{admin.username}</span>
                  <span className="supervisor-edit-option__email">{admin.email}</span>
                </label>
              );
            })
          )}
        </div>

        {required && selectedIds.length === 0 && (
          <div className="supervisor-edit-modal__warning">
            Au moins un superviseur est requis pour un utilisateur.
          </div>
        )}

        <div className="supervisor-edit-modal__actions">
          <button
            className="supervisor-edit-modal__btn supervisor-edit-modal__btn--cancel"
            onClick={onClose}
            disabled={saving}
          >
            Annuler
          </button>
          <button
            className="supervisor-edit-modal__btn supervisor-edit-modal__btn--save"
            onClick={handleSave}
            disabled={saving || !hasChanges}
          >
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>

      </div>
    </div>
  );
};

export default SupervisorEditModal;